import { Checkbox, Tag, Tooltip } from "antd";
import type { KeyboardEvent, MouseEvent } from "react";

import type { ProjectListItem, ProjectStatus } from "../../api/projects";
import { ProjectActionsMenu } from "./ProjectActionsMenu";
import { ProjectFolderIcon } from "./ProjectFolderIcon";

const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  SUBMITTED: "已提交",
  COMPLETED: "已完成",
};

interface ProjectCardProps {
  project: ProjectListItem;
  workTypeLabel: string;
  isAdmin: boolean;
  recycleBin: boolean;
  selected: boolean;
  onSelect: (projectId: string, selected: boolean) => void;
  onView: (project: ProjectListItem) => void;
  onEdit: (project: ProjectListItem) => void;
  onChangeStatus: (project: ProjectListItem, status: ProjectStatus) => void;
  onDelete: (project: ProjectListItem) => void;
  onRestore: (project: ProjectListItem) => void;
}

export function ProjectCard({
  project,
  workTypeLabel,
  isAdmin,
  recycleBin,
  selected,
  onSelect,
  onView,
  onEdit,
  onChangeStatus,
  onDelete,
  onRestore,
}: ProjectCardProps) {
  const openProject = () => onView(project);
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openProject();
    }
  };
  const stopPropagation = (event: MouseEvent) => event.stopPropagation();

  return (
    <article
      className={`project-folder-card${selected ? " project-folder-card--selected" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`查看项目：${project.work_name}`}
      onClick={openProject}
      onKeyDown={handleKeyDown}
    >
      {isAdmin && !recycleBin ? (
        <Checkbox
          className="project-card-checkbox"
          checked={selected}
          aria-label={`选择项目：${project.work_name}`}
          onClick={stopPropagation}
          onChange={(event) => onSelect(project.id, event.target.checked)}
        />
      ) : null}

      <Tag
        className={`project-card-status project-card-status--${project.status.toLowerCase()}`}
        bordered={false}
      >
        {PROJECT_STATUS_LABELS[project.status]}
      </Tag>

      <ProjectActionsMenu
        project={project}
        isAdmin={isAdmin}
        recycleBin={recycleBin}
        className="project-card-menu"
        onView={onView}
        onEdit={onEdit}
        onChangeStatus={onChangeStatus}
        onDelete={onDelete}
        onRestore={onRestore}
      />

      <ProjectFolderIcon />

      <Tooltip title={project.work_name} placement="bottom">
        <strong className="project-card-title">{project.work_name}</strong>
      </Tooltip>
      <div className="project-card-meta">
        <span>{workTypeLabel}</span>
        <i aria-hidden="true">·</i>
        <span>更新于 {project.updated_at.slice(0, 10)}</span>
      </div>
    </article>
  );
}
